import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../../lib/result-codes";
import { getMerchantRewardSummary } from "../api/merchantCouponList.api";

interface MerchantCouponSummary {
  totalActiveCoupons: number;
  totalCoupons: number;
  totalViewedCoupons: number;
  couponsRedeemed: number;
  usageCount: number;
  lowOutOfStock: number;
}

interface UseGetMerchantRewardSummaryReturn {
  summary: MerchantCouponSummary;
  loading: boolean;
  error: string | null;
  refreshSummary: () => Promise<void>;
}

const EMPTY_SUMMARY: MerchantCouponSummary = {
  totalActiveCoupons: 0,
  totalCoupons: 0,
  totalViewedCoupons: 0,
  couponsRedeemed: 0,
  usageCount: 0,
  lowOutOfStock: 0,
};

export const useGetMerchantRewardSummary = (): UseGetMerchantRewardSummaryReturn => {
  const [summary, setSummary] = useState<MerchantCouponSummary>(EMPTY_SUMMARY);
  const [error, setError] = useState<string | null>(null);
  const { run: runLatestRequest, loading } = useLatestRequest();

  const fetchSummary = useCallback(async () => {
    try {
      setError(null);
      const response = await runLatestRequest((signal) => getMerchantRewardSummary(signal));

      if (!response) return;
      setSummary({
        totalActiveCoupons: response.total_active_coupons ?? 0,
        totalCoupons: response.total_coupons ?? 0,
        totalViewedCoupons: response.total_viewed_coupons ?? 0,
        couponsRedeemed: response.coupons_redeemed ?? 0,
        usageCount: response.usage_count ?? 0,
        lowOutOfStock: response.low_or_out_of_stock ?? 0,
      });
    } catch (fetchError: unknown) {
      setError(getApiErrorMessage(fetchError, "Failed to load summary"));
    }
  }, [runLatestRequest]);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  return {
    summary,
    loading,
    error,
    refreshSummary: fetchSummary,
  };
};
